import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, type QueryClient, type UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { fetchAlarmDelete, fetchAlarmIsRead, fetchAlarmList } from '../../apis/client-side/alarm';
import type { AlarmDeleteParams, AlarmIsReadResponse, AlarmListResponse } from '../../apis/client-side/alarm/type';
import type { ApiBaseResponse } from '../../apis/type';
import { itemsPerQuery } from '../../constants/pagenation';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { alarmQueryKeys } from './alarmQueryKeys';

// 알림 목록은 앱마다 손수 만들던 무한쿼리를 여기로 수렴시킨다. 쿼리키를 alarmQueryKeys 네임스페이스
// 아래(`['alarm','fetchAlarmList',...]`)로 두어야 개별/전체 읽음·삭제 후 `alarmQueryKeys._def`
// 무효화가 목록+is_read(벨)를 한 번에 갱신한다.
export const useAlarmListInfiniteQuery = (category, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== category) {
    t0 = alarmQueryKeys.fetchAlarmList({
      limit: itemsPerQuery,
      category
    });
    $[0] = category;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = options?.enabled;
  let t2;
  if ($[2] !== category) {
    t2 = async t3 => {
      const {
        pageParam
      } = t3;
      const params = {
        limit: itemsPerQuery,
        ...(category && {
          category
        }),
        ...(pageParam?.lastId && {
          lastId: pageParam.lastId
        })
      };
      return fetchAlarmList(params);
    };
    $[2] = category;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0.queryKey || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      initialPageParam: null as null | {
        lastId: string;
      },
      queryKey: t0.queryKey,
      staleTime: TimeConstants.HALF_SECOND * 10,
      enabled: t1,
      queryFn: t2,
      getNextPageParam: _temp,
      select: _temp3
    };
    $[4] = t0.queryKey;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useInfiniteQuery(t3);
};

// 읽음 처리 후 목록을 서버 재조회(invalidate → refetch)로 갱신하면, list_read(POST) 커밋과
// list(GET) 사이의 read-after-write 레이스 + stale-while-revalidate 프레임 때문에 레드닷이
// 남는다. status 전이는 클라이언트가 이미 아는 사실이므로 목록 캐시를 직접 낙관적으로 갱신하고,
// 서버 진실이 필요한 벨(is_read)만 무효화한다. objIds 미지정이면 전체 읽음.
export const markAlarmReadInCache = (queryClient: QueryClient, objIds?: string[]) => {
  const targets = objIds ? new Set(objIds) : null;
  queryClient.setQueriesData<InfiniteData<AlarmListResponse>>({
    queryKey: alarmQueryKeys.fetchAlarmList._def
  }, old => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map(page => ({
        ...page,
        list: (page.list ?? []).map(item => item.status === 'unread' && (!targets || targets.has(item.objId)) ? {
          ...item,
          status: 'read' as const
        } : item)
      }))
    };
  });
  queryClient.invalidateQueries({
    queryKey: alarmQueryKeys.fetchAlarmIsRead().queryKey
  });
};
export const useAlarmDeleteMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = alarmQueryKeys.fetchAlarmDelete();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp4,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useAlarmIsRead = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = alarmQueryKeys.fetchAlarmIsRead();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0.queryKey,
      queryFn: _temp5,
      staleTime: TimeConstants.ONE_MINUTE,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
function _temp(lastPage) {
  if (!lastPage?.lastId) {
    return null;
  }
  return {
    lastId: lastPage.lastId
  };
}
function _temp2(page) {
  return page.list ?? [];
}
function _temp3(data) {
  return {
    ...data,
    lastId: data?.pages[data.pages.length - 1].lastId,
    list: data?.pages.flatMap(_temp2),
    expireDay: data?.pages[data.pages.length - 1].expireDay
  };
}
function _temp4(addBookmarkParams) {
  return fetchAlarmDelete(addBookmarkParams);
}
function _temp5() {
  return fetchAlarmIsRead();
}